import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import toast from 'react-hot-toast';

/* ─── Types ──────────────────────────────────── */
interface ChatRoom {
  id: string;
  box_id: string | null;
  name: string;
  type: string;
  unread_count?: number;
  message_count?: number;
  last_message?: string;
  last_activity?: string;
  closed_at?: string | null;
  created_at: string;
}

interface ChatMessage {
  id: string;
  room_id: string;
  sender_id: string;
  sender_name?: string;
  body: string;
  created_at: string;
}

export default function ChatPage() {
  const [selectedRoom, setSelectedRoom] = useState<string | null>(null);
  const [msgText, setMsgText] = useState('');
  const [roomSearch, setRoomSearch] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const { data: roomsData, isLoading: roomsLoading } = useQuery({
    queryKey: ['chat-rooms'],
    queryFn: () => api.get('/chat/rooms').then((r) => r.data),
    refetchInterval: 10000,
  });

  const { data: messagesData, refetch: refetchMessages } = useQuery({
    queryKey: ['chat-messages', selectedRoom],
    queryFn: () => selectedRoom ? api.get(`/chat/rooms/${selectedRoom}`).then((r) => r.data) : null,
    enabled: !!selectedRoom,
    refetchInterval: 5000,
  });

  const { data: unreadData } = useQuery({
    queryKey: ['chat-unread'],
    queryFn: () => api.get('/chat/unread').then((r) => r.data),
    refetchInterval: 15000,
  });

  const sendMutation = useMutation({
    mutationFn: (body: string) => api.post(`/chat/rooms/${selectedRoom}`, { body }),
    onSuccess: () => {
      setMsgText('');
      refetchMessages();
      queryClient.invalidateQueries({ queryKey: ['chat-rooms'] });
    },
  });

  const supportMutation = useMutation({
    mutationFn: (subject: string) => api.post('/chat/support', { subject }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['chat-rooms'] }),
  });

  const deleteMessageMutation = useMutation({
    mutationFn: (messageId: string) => api.delete(`/chat/admin/messages/${messageId}`),
    onSuccess: () => {
      toast.success('Messaggio eliminato');
      refetchMessages();
    },
    onError: () => toast.error('Errore eliminazione messaggio'),
  });

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messagesData]);

  const rooms: ChatRoom[] = roomsData?.rooms ?? [];
  const messages: ChatMessage[] = messagesData?.messages ?? [];
  const totalUnread: number = unreadData?.unread ?? 0;

  const filteredRooms = roomSearch
    ? rooms.filter((r) => r.name.toLowerCase().includes(roomSearch.toLowerCase()))
    : rooms;

  const selectedRoomData = rooms.find((r) => r.id === selectedRoom);
  const isRoomClosed = !!selectedRoomData?.closed_at;

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (msgText.trim() && selectedRoom && !isRoomClosed) {
      sendMutation.mutate(msgText.trim());
    }
  };

  const timeFormat = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      <Helmet><title>Chat — React-City</title></Helmet>

      <div className="flex h-[calc(100vh-10rem)] bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-700 overflow-hidden animate-in">
        {/* Sidebar rooms */}
        <div className="w-72 border-r border-surface-200 dark:border-surface-700 flex flex-col">
          <div className="p-4 border-b border-surface-100 dark:border-surface-800">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-semibold text-surface-900 dark:text-white text-sm">
                Chat
                {totalUnread > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-red-500 rounded-full">
                    {totalUnread > 99 ? '99+' : totalUnread}
                  </span>
                )}
              </h2>
              <button
                onClick={() => {
                  const subject = prompt('Oggetto della richiesta:');
                  if (subject) supportMutation.mutate(subject);
                }}
                className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
              >
                + Supporto
              </button>
            </div>
            <input
              type="text"
              placeholder="🔍 Cerca chat…"
              value={roomSearch}
              onChange={(e) => setRoomSearch(e.target.value)}
              className="w-full px-3 py-1.5 rounded-lg bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-xs text-surface-900 dark:text-white placeholder-surface-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div className="flex-1 overflow-y-auto">
            {roomsLoading ? (
              <div className="p-4 text-center text-surface-400 text-sm">Caricamento...</div>
            ) : filteredRooms.length === 0 ? (
              <div className="p-6 text-center">
                <p className="text-surface-400 text-sm mb-3">
                  {roomSearch ? 'Nessun risultato' : 'Nessuna chat attiva'}
                </p>
                {!roomSearch && (
                  <div className="space-y-2">
                    <p className="text-xs text-surface-400">Entra in un box o crea una richiesta di supporto</p>
                    <button
                      onClick={() => {
                        const subject = prompt('Oggetto della richiesta:');
                        if (subject) supportMutation.mutate(subject);
                      }}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      🎧 Apri chat supporto
                    </button>
                  </div>
                )}
              </div>
            ) : (
              filteredRooms.map((room) => (
                <button
                  key={room.id}
                  onClick={() => setSelectedRoom(room.id)}
                  className={`w-full px-4 py-3 text-left hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors border-b border-surface-50 dark:border-surface-800 ${
                    selectedRoom === room.id ? 'bg-primary-50/50 dark:bg-primary-500/5' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{room.type === 'support' ? '🎧' : '💬'}</span>
                    <span className="text-sm font-medium text-surface-900 dark:text-white truncate flex-1">
                      {room.name}
                    </span>
                    {(room.unread_count ?? 0) > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
                        {room.unread_count}
                      </span>
                    )}
                    {room.closed_at && (
                      <span className="text-[10px] text-red-400">🔒</span>
                    )}
                  </div>
                  {room.last_message && (
                    <p className="text-xs text-surface-400 truncate mt-1 ml-6">
                      {room.last_message}
                    </p>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Messages area */}
        <div className="flex-1 flex flex-col">
          {selectedRoom ? (
            <>
              {/* Room header */}
              <div className="px-4 py-3 border-b border-surface-100 dark:border-surface-800 flex items-center justify-between">
                <h3 className="font-semibold text-sm text-surface-900 dark:text-white">
                  {selectedRoomData?.name ?? 'Chat'}
                  {isRoomClosed && <span className="ml-2 text-xs text-red-400">🔒 Chiusa</span>}
                </h3>
                {isAdmin && !isRoomClosed && (
                  <button
                    onClick={() => {
                      if (confirm('Chiudere questa room?')) {
                        api.put(`/chat/admin/rooms/${selectedRoom}/close`).then(() => {
                          toast.success('Room chiusa');
                          queryClient.invalidateQueries({ queryKey: ['chat-rooms'] });
                        });
                      }
                    }}
                    className="text-xs text-red-500 hover:text-red-400"
                  >
                    🔒 Chiudi room
                  </button>
                )}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map((msg) => (
                  <div key={msg.id} className="flex gap-2 group">
                    <div className="w-7 h-7 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center text-xs font-bold text-primary-700 dark:text-primary-300 flex-shrink-0">
                      {(msg.sender_name ?? '?')[0]}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs font-semibold text-surface-900 dark:text-white">
                          {msg.sender_name ?? 'Utente'}
                        </span>
                        <span className="text-[10px] text-surface-400">
                          {timeFormat(msg.created_at)}
                        </span>
                        {isAdmin && (
                          <button
                            onClick={() => { if (confirm('Eliminare questo messaggio?')) deleteMessageMutation.mutate(msg.id); }}
                            className="opacity-0 group-hover:opacity-100 text-[10px] text-red-400 hover:text-red-300 transition-opacity"
                            title="Elimina messaggio (admin)"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                      <p className="text-sm text-surface-700 dark:text-surface-300 mt-0.5">
                        {msg.body}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              {isRoomClosed ? (
                <div className="p-3 border-t border-surface-100 dark:border-surface-800 text-center text-sm text-surface-400">
                  🔒 Questa chat è stata chiusa
                </div>
              ) : (
                <form onSubmit={handleSend} className="p-3 border-t border-surface-100 dark:border-surface-800 flex gap-2">
                  <input
                    type="text"
                    value={msgText}
                    onChange={(e) => setMsgText(e.target.value)}
                    placeholder="Scrivi un messaggio..."
                    className="flex-1 px-4 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-sm text-surface-900 dark:text-white placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <button
                    type="submit"
                    disabled={!msgText.trim() || sendMutation.isPending}
                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-sm font-medium disabled:opacity-50 transition-colors"
                  >
                    Invia
                  </button>
                </form>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-surface-400 dark:text-surface-500">
              <div className="text-center">
                <span className="text-4xl mb-3 block">💬</span>
                <p className="text-sm">Seleziona una chat per iniziare</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
